'use client'

import { FC, useCallback, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import * as Yup from 'yup'

import { METHOD_2FA_ENUM } from '@/common/constants'
import { IApiError } from '@/common/interfaces'
import { Button, Checkbox, Input, ModalBase } from '@/components/commons'
import { OnlypayLogoCompleteNewIcon } from '@/components/icons'
import { useAuthProvider } from '@/contexts/auth/AuthProvider'
import { useMultiTenantAuthenticationService } from '@/services/auth/multiTenantAuthenticationService/multiTenantAuthenticationService'
import type { IMultiTenantBasicInfoResponse } from '@/services/auth/multiTenantBasicInfoService/interfaces/response'
import { useMultiTenantBasicInfoService } from '@/services/auth/multiTenantBasicInfoService/multiTenantBasicInfoService'
import { useMultiTenantCreate2FAService } from '@/services/auth/multiTenantCreate2FAService/multiTenantCreate2FAService'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  CodeForm,
  ContactForm,
  ForgotPasswordModal,
  LoginAttemptsExceededModal,
  MultiAgencySelectionPage,
  SlideTransition,
} from '../'
import ModalInformation from '../../../ModalInformation'
import { FORM_CASE_ENUM } from '../../utils/constants'
import { INewSignInFormProps } from './types'

// Schema para novo fluxo (apenas CPF)
const schemaCpfOnly = Yup.object({
  userName: Yup.string().required('Usuário é obrigatório'),
  pwd: Yup.string(),
  rememberMe: Yup.boolean().notRequired(),
})

type NewFormProps = Yup.InferType<typeof schemaCpfOnly>

/** Tenant enriquecido com contatos do 200 MultiTenantAuthentication (uso no modal 2FA). */
interface ITenantFor2FA {
  tenantId: string
  email: string
  celPhoneNumber: string
}

const NewSignInForm: FC<INewSignInFormProps> = ({
  isRememberMe,
  savedLoginUser,
}) => {
  const {
    login2FA,
    customMessageError,
    isOpenModal,
    setIsOpenModal,
    modalVariant,
    setCustomMessageError,
    setModalVariant,
  } = useAuthProvider()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingLogin, setIsLoadingLogin] = useState(false)
  const [formCase, setFormCase] = useState<FORM_CASE_ENUM>(
    FORM_CASE_ENUM.CPF_ONLY,
  )
  const [showModalMethod, setShowModalMethod] = useState<boolean>(false)
  const [currentMethod, setCurrentMethod] = useState<METHOD_2FA_ENUM>(
    METHOD_2FA_ENUM.EMAIL,
  )

  // Estados para novo fluxo multi-tenant (contatos vêm do 200 MultiTenantAuthentication)
  const [selectedTenant, setSelectedTenant] = useState<ITenantFor2FA | null>(
    null,
  )
  const [multiTenantData, setMultiTenantData] =
    useState<IMultiTenantBasicInfoResponse | null>(null)
  const [showAgencySelection, setShowAgencySelection] = useState(false)
  const [userNameValue, setUserNameValue] = useState('')
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false)
  const [forgotPasswordTenantId, setForgotPasswordTenantId] = useState<
    string | null
  >(null)
  const [
    isLoginAttemptsExceededModalOpen,
    setIsLoginAttemptsExceededModalOpen,
  ] = useState(false)
  const [loginAttemptsExceededTenantId, setLoginAttemptsExceededTenantId] =
    useState<string | null>(null)
  const [clearPasswordSignal, setClearPasswordSignal] = useState(0)

  const { mutateAsync: multiTenantBasicInfoMutateAsync } =
    useMultiTenantBasicInfoService()
  const { mutateAsync: multiTenantAuthenticationMutateAsync } =
    useMultiTenantAuthenticationService()
  const {
    mutateAsync: multiTenantCreate2FAMutateAsync,
    isPending: multiTenantCreate2FAIsPending,
  } = useMultiTenantCreate2FAService()

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<NewFormProps>({
    mode: 'all',
    resolver: yupResolver(schemaCpfOnly),
    defaultValues: {
      userName: savedLoginUser ?? '',
      pwd: '',
      rememberMe: isRememberMe,
    },
  })

  /**
   * Após redefinir a senha, limpa o input de senha ao voltar para o login
   */
  function handlePasswordResetGoToLogin() {
    setValue('pwd', '')
    setClearPasswordSignal((prev) => prev + 1)
  }

  /**
   * Busca as agências do usuário
   */
  async function handleGetMultiTenantBasicInfo() {
    const { userName } = getValues()
    setIsLoading(true)
    setUserNameValue(userName)

    await multiTenantBasicInfoMutateAsync({ userName })
      .then((response) => {
        setMultiTenantData(response)
        // Sempre mostra a tela de seleção de agência
        setShowAgencySelection(true)
      })
      .catch((error) => {
        setCustomMessageError(
          error?.response?.data ?? 'Erro ao buscar as agências do usuário.',
        )
        setModalVariant('customMessage')
        setIsOpenModal(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  /**
   * Submete o formulário inicial (CPF)
   */
  const onSubmit: SubmitHandler<NewFormProps> = async () => {
    await handleGetMultiTenantBasicInfo()
  }

  /**
   * Handler chamado quando o usuário continua da tela de seleção de agência.
   * Valida senha via MultiTenantAuthentication; em 200, usa contatos da resposta no modal 2FA.
   */
  const handleContinueFromAgencySelection = useCallback(
    async (tenantId: string, pwd: string, rememberMe: boolean) => {
      const userName = userNameValue || getValues('userName')
      if (!userName) return

      try {
        const contacts = await multiTenantAuthenticationMutateAsync({
          userName,
          pwd,
          tenantId,
        })

        setSelectedTenant(contacts)
        setValue('pwd', pwd)
        setValue('rememberMe', rememberMe)
        setShowModalMethod(true)
        setFormCase(FORM_CASE_ENUM.CONTACT_FORM)
      } catch (error: unknown) {
        const err = error as { response?: { data?: string | IApiError[] } }
        const errorData = err?.response?.data
        const hasAttemptsExceeded =
          Array.isArray(errorData) &&
          errorData.some((item) => item.key === 'number-attempts-exceeded')

        if (hasAttemptsExceeded) {
          setLoginAttemptsExceededTenantId(tenantId)
          setIsLoginAttemptsExceededModalOpen(true)
          return
        }

        setCustomMessageError(
          (errorData ?? 'Usuário ou senha inválidos.') as string | IApiError[],
        )
        setModalVariant('customMessage')
        setIsOpenModal(true)
      }
    },
    [
      userNameValue,
      getValues,
      multiTenantAuthenticationMutateAsync,
      setValue,
      setCustomMessageError,
      setModalVariant,
      setIsOpenModal,
    ],
  )

  /**
   * Envia o código de acesso 2FA para o contato escolhido
   * Atualiza o state formCase para FORM_CASE_ENUM.CODE_FORM
   */
  const handleSendCode = useCallback(
    async (method?: METHOD_2FA_ENUM) => {
      const { pwd, userName } = getValues()
      const methodSerialized = {
        method: currentMethod,
      }

      if (method) {
        methodSerialized.method = method
        setCurrentMethod(method)
      }

      if (!selectedTenant) return

      await multiTenantCreate2FAMutateAsync({
        method2FA: methodSerialized.method,
        pwd: pwd ?? '',
        userIp: '000.000.000.000',
        userName,
        tenantId: selectedTenant.tenantId,
      })
        .then(() => {
          setFormCase(FORM_CASE_ENUM.CODE_FORM)
        })
        .catch((error) => {
          setCustomMessageError(
            error?.response?.data ?? 'Erro ao enviar o código de autenticação.',
          )
          setModalVariant('customMessage')
          setIsOpenModal(true)
        })
    },
    [
      multiTenantCreate2FAMutateAsync,
      selectedTenant,
      setCustomMessageError,
      setModalVariant,
      setIsOpenModal,
      setFormCase,
      currentMethod,
      setCurrentMethod,
      getValues,
    ],
  )

  /**
   * Valida o código recebido e faz o login
   */
  const handleConfirmationCode = useCallback(
    async (code: string) => {
      const { pwd, userName, rememberMe } = getValues()
      setIsLoadingLogin(true)

      if (!selectedTenant) return

      await login2FA({
        pwd: pwd ?? '',
        userName,
        rememberMe,
        code,
        tenantId: selectedTenant.tenantId,
      }).finally(() => {
        setIsLoadingLogin(false)
      })
    },
    [login2FA, selectedTenant, getValues],
  )

  return (
    <>
      <div className="relative w-full overflow-hidden h-full">
        {/* Formulário de CPF */}
        <SlideTransition
          isVisible={!showAgencySelection}
          direction="left"
          className="absolute inset-0 w-full flex justify-center items-center"
        >
          <form
            className="flex flex-col w-full bg-white gap-8"
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
          >
            <OnlypayLogoCompleteNewIcon className="self-center md:self-start" />

            <h1 className="prose-headline-h6 text-primary-900 text-center md:text-start">
              Informe o CPF para selecionar Agência
            </h1>

            <Input
              {...register('userName')}
              autoCapitalize="none"
              autoComplete="off"
              label="Usuário"
              mask="999.999.999-99"
              placeholder="000.000.000-00"
              onlyNums
              wFull
              helperText={errors.userName?.message}
            />

            <Button
              text="Continuar"
              type="submit"
              className="w-full"
              onClick={(e) => {
                if (isLoading) {
                  e.preventDefault()
                }
              }}
              isLoading={isLoading}
            />

            <div className="flex w-full items-center justify-between gap-8">
              <Checkbox {...register('rememberMe')} label="Lembrar de mim?" />
            </div>

            <div className="flex w-full items-center justify-center gap-8">
              <p className="prose-body-small text-primary-900 text-center md:text-start">
                Ainda não tem uma conta? Fale com o seu administrador.
              </p>
            </div>
          </form>
        </SlideTransition>

        {/* Tela de seleção de agências */}
        {multiTenantData && (
          <SlideTransition
            isVisible={showAgencySelection}
            direction="left"
            className="absolute inset-0 w-full flex justify-center items-center"
          >
            <MultiAgencySelectionPage
              multiTenantData={multiTenantData}
              userName={userNameValue}
              isRememberMe={isRememberMe}
              savedLoginUser={savedLoginUser}
              onContinue={handleContinueFromAgencySelection}
              onBack={() => setShowAgencySelection(false)}
              onForgotPassword={(tenantId) => {
                setForgotPasswordTenantId(tenantId)
                setIsForgotPasswordModalOpen(true)
              }}
              clearPasswordSignal={clearPasswordSignal}
            />
          </SlideTransition>
        )}
      </div>

      {/* Modais */}
      {showModalMethod && (
        <ModalBase
          className="w-full max-w-[464px]"
          isOpenModal={showModalMethod}
        >
          <ModalBase.Body className="justify-end">
            {formCase === FORM_CASE_ENUM.CONTACT_FORM && selectedTenant && (
              <ContactForm
                sendButtonLoading={multiTenantCreate2FAIsPending}
                setFormCase={setFormCase}
                contacts={{
                  email: selectedTenant.email,
                  celPhoneNumber: selectedTenant.celPhoneNumber,
                  method2Fa: METHOD_2FA_ENUM.EMAIL,
                }}
                currentMethod={currentMethod}
                setIsOpenModal={setShowModalMethod}
                handleSendCode={handleSendCode}
                setCurrentMethod={setCurrentMethod}
              />
            )}
            {formCase !== FORM_CASE_ENUM.CONTACT_FORM && selectedTenant && (
              <CodeForm
                sendButtonLoading={isLoadingLogin}
                setFormCase={setFormCase}
                contacts={{
                  email: selectedTenant.email,
                  celPhoneNumber: selectedTenant.celPhoneNumber,
                  method2Fa: METHOD_2FA_ENUM.EMAIL,
                }}
                currentMethod={currentMethod}
                setIsOpenModal={setShowModalMethod}
                handleConfirmationCode={handleConfirmationCode}
                onResend={handleSendCode}
                haveACode={formCase === FORM_CASE_ENUM.HAS_A_CODE}
              />
            )}
          </ModalBase.Body>
        </ModalBase>
      )}

      {isOpenModal && (
        <ModalInformation
          variant={modalVariant}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          customMessage={customMessageError}
        />
      )}

      {/* Modal de Esqueci minha senha */}
      {forgotPasswordTenantId && (
        <ForgotPasswordModal
          isOpenModal={isForgotPasswordModalOpen}
          setIsOpenModal={setIsForgotPasswordModalOpen}
          document={userNameValue}
          tenantId={forgotPasswordTenantId}
        />
      )}

      {/* Modal de tentativas de login excedidas */}
      {loginAttemptsExceededTenantId && (
        <LoginAttemptsExceededModal
          isOpenModal={isLoginAttemptsExceededModalOpen}
          setIsOpenModal={setIsLoginAttemptsExceededModalOpen}
          document={userNameValue}
          tenantId={loginAttemptsExceededTenantId}
          onGoToLogin={handlePasswordResetGoToLogin}
        />
      )}
    </>
  )
}

export default NewSignInForm
